import { useMutation, useQuery, useQueryClient, keepPreviousData, type UseQueryResult } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { salesInvoicesApi } from './api';
import type {
  RecordSalesReceiptPayload, SalesInvoice, SalesInvoiceListParams, SalesInvoiceListResponse, SalesInvoiceStats,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useSalesInvoices(params: SalesInvoiceListParams): UseQueryResult<SalesInvoiceListResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['sales-invoices', tenantId, 'list', params],
    queryFn: () => salesInvoicesApi.list(params),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useSalesInvoiceStats(locationId?: string): UseQueryResult<SalesInvoiceStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['sales-invoices', tenantId, 'stats', locationId ?? ''],
    queryFn: () => salesInvoicesApi.stats(locationId),
    staleTime: 15_000,
  });
}

export function useSalesInvoice(id: string | undefined): UseQueryResult<SalesInvoice | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['sales-invoices', tenantId, 'detail', id],
    queryFn: () => (id ? salesInvoicesApi.getById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

/** Money moves on the sales order too (its balances, KPIs and detail), so both prefixes are refreshed. */
function useInvalidate() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['sales-invoices', tenantId] });
    void qc.invalidateQueries({ queryKey: ['sales-orders', tenantId] });
  };
}

export function useRecordSalesInvoicePayment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ soId, invoiceId, payload }: { soId: string; invoiceId: string; payload: RecordSalesReceiptPayload }) =>
      salesInvoicesApi.recordPayment(soId, invoiceId, payload),
    onSuccess: () => invalidate(),
  });
}

export function useRecordSalesAdvance() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ soId, payload }: { soId: string; payload: RecordSalesReceiptPayload }) => salesInvoicesApi.recordAdvance(soId, payload),
    onSuccess: () => invalidate(),
  });
}

export function useCancelSalesInvoice() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ soId, invoiceId, reason }: { soId: string; invoiceId: string; reason?: string }) =>
      salesInvoicesApi.cancel(soId, invoiceId, reason),
    onSuccess: () => invalidate(),
  });
}

/** Fetches the invoice PDF blob; read-only, no invalidation. */
export function useDownloadSalesInvoice() {
  return useMutation({ mutationFn: (id: string) => salesInvoicesApi.downloadPdf(id) });
}
